import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Plus, Trash, FloppyDisk, ArrowUp, ArrowDown, CheckCircle, GitBranch, User, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

const REQUEST_TYPES = [
  { v: "leave", label: "Leave" },
  { v: "reimbursement", label: "Reimbursement" },
  { v: "regularization", label: "Attendance regularization" },
];
const APPROVER_TYPES = [
  { v: "manager", label: "Reporting manager (dynamic)" },
  { v: "role", label: "By role" },
  { v: "employee", label: "Specific employee" },
];
const ROLES = ["manager", "hr", "company_admin"];
const CONDITIONS = [
  { v: "__none", label: "Always" },
  { v: "amount_gte:5000", label: "Only if amount ≥ ₹5,000" },
  { v: "amount_gte:10000", label: "Only if amount ≥ ₹10,000" },
  { v: "amount_gte:25000", label: "Only if amount ≥ ₹25,000" },
  { v: "days_gte:3", label: "Only if ≥ 3 days" },
  { v: "days_gte:5", label: "Only if ≥ 5 days" },
];

export default function ApprovalChainsPanel({ params }) {
  const [rt, setRt] = useState("leave");
  const [chain, setChain] = useState({ request_type: "leave", stages: [] });
  const [busy, setBusy] = useState(false);
  const [employees, setEmployees] = useState([]);

  const load = () => {
    api.get(`/approval-chains/${rt}`, { params }).then(({ data }) => setChain({ ...data, stages: data.stages || [] })).catch(() => setChain({ request_type: rt, stages: [] }));
    api.get("/employees", { params }).then(({ data }) => setEmployees(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rt, params.tenant_id]);

  const addStage = () => {
    const nextOrder = (chain.stages || []).length;
    setChain({ ...chain, stages: [...(chain.stages || []), { order: nextOrder, approver_type: "manager", value: "reporting_manager", label: "Reporting manager", condition: null }] });
  };
  const updateStage = (i, patch) => {
    const stages = [...chain.stages];
    stages[i] = { ...stages[i], ...patch };
    setChain({ ...chain, stages });
  };
  const removeStage = (i) => {
    const stages = chain.stages.filter((_, j) => j !== i).map((s, k) => ({ ...s, order: k }));
    setChain({ ...chain, stages });
  };
  const move = (i, dir) => {
    const stages = [...chain.stages];
    const j = i + dir;
    if (j < 0 || j >= stages.length) return;
    [stages[i], stages[j]] = [stages[j], stages[i]];
    setChain({ ...chain, stages: stages.map((s, k) => ({ ...s, order: k })) });
  };
  const save = async () => {
    setBusy(true);
    try {
      // Ensure defaults per approver_type
      const cleanStages = chain.stages.map((s, i) => {
        const stage = { ...s, order: i };
        if (stage.approver_type === "manager") stage.value = "reporting_manager";
        if (stage.approver_type === "role" && !ROLES.includes(stage.value)) stage.value = "hr";
        if (!stage.label) stage.label = APPROVER_TYPES.find((a) => a.v === stage.approver_type)?.label || stage.value;
        return stage;
      });
      await api.post("/approval-chains", { ...params, request_type: rt, stages: cleanStages });
      toast.success("Chain saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display font-bold text-lg flex items-center gap-2"><GitBranch size={18} className="text-adb-navy" /> Approval chains</div>
          <div className="text-xs text-zinc-500">Configure multi-level approval flows per request type. Requests advance stage-by-stage; final stage marks request approved.</div>
        </div>
        <Tabs value={rt} onValueChange={setRt}>
          <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
            {REQUEST_TYPES.map((t) => (
              <TabsTrigger key={t.v} value={t.v} className="rounded-sm" data-testid={`chain-tab-${t.v}`}>{t.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {chain.is_default && (
        <div className="border border-sky-200 bg-sky-50 rounded-sm p-3 text-xs text-sky-800 flex items-start gap-2">
          <Info size={14} className="mt-0.5 shrink-0" />
          <div>Using default single-stage chain (reporting manager). Add stages and save to customise.</div>
        </div>
      )}

      <div className="border border-zinc-200 bg-white rounded-sm">
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
          <div className="text-sm font-semibold">Stages · {chain.stages?.length || 0}</div>
          <Button size="sm" variant="outline" onClick={addStage} className="rounded-sm" data-testid="chain-add-stage"><Plus size={12} className="mr-1" /> Add stage</Button>
        </div>
        {(chain.stages || []).length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">No stages defined. Add at least one stage.</div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {chain.stages.map((s, i) => (
              <div key={i} className="p-3 grid grid-cols-12 gap-2 items-center" data-testid={`chain-stage-${i}`}>
                <div className="col-span-1 flex items-center gap-1">
                  <div className="w-7 h-7 rounded-full bg-adb-navy text-white grid place-items-center text-xs font-bold">{i + 1}</div>
                </div>
                <div className="col-span-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Approver</div>
                  <Select value={s.approver_type} onValueChange={(v) => updateStage(i, { approver_type: v, value: v === "manager" ? "reporting_manager" : v === "role" ? "hr" : "" })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`chain-approver-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{APPROVER_TYPES.map((a) => <SelectItem key={a.v} value={a.v}>{a.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-3">
                  {s.approver_type === "role" && (
                    <>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Role</div>
                      <Select value={s.value} onValueChange={(v) => updateStage(i, { value: v })}>
                        <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`chain-role-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}</SelectContent>
                      </Select>
                    </>
                  )}
                  {s.approver_type === "employee" && (
                    <>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Employee</div>
                      <Select value={s.value || "__unset"} onValueChange={(v) => updateStage(i, { value: v === "__unset" ? "" : v })}>
                        <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`chain-employee-${i}`}><SelectValue placeholder="Pick employee" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__unset">— Not selected —</SelectItem>
                          {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                  {s.approver_type === "manager" && (
                    <div className="mt-3">
                      <Badge variant="outline" className="rounded-sm bg-adb-navy/5 border-adb-navy/20 text-adb-navy text-[10px]"><User size={10} className="inline mr-1" /> Employee's reporting manager</Badge>
                    </div>
                  )}
                </div>
                <div className="col-span-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Condition</div>
                  <Select value={s.condition || "__none"} onValueChange={(v) => updateStage(i, { condition: v === "__none" ? null : v })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid={`chain-cond-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{CONDITIONS.filter((c) => rt === "reimbursement" || !c.v.startsWith("amount")).filter((c) => rt === "leave" || !c.v.startsWith("days")).map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Label</div>
                  <Input value={s.label || ""} onChange={(e) => updateStage(i, { label: e.target.value })} placeholder="e.g. Manager review" className="rounded-sm h-8 text-xs" data-testid={`chain-label-${i}`} />
                </div>
                <div className="col-span-3 md:col-span-12 lg:col-span-12 xl:col-span-12"></div>
                <div className="col-span-12 flex justify-end gap-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 border border-zinc-200 rounded-sm hover:bg-zinc-50 disabled:opacity-30" data-testid={`chain-up-${i}`}><ArrowUp size={12} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === chain.stages.length - 1} className="p-1 border border-zinc-200 rounded-sm hover:bg-zinc-50 disabled:opacity-30" data-testid={`chain-down-${i}`}><ArrowDown size={12} /></button>
                  <button onClick={() => removeStage(i)} className="p-1 border border-red-200 text-red-700 rounded-sm hover:bg-red-50" data-testid={`chain-del-${i}`}><Trash size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="px-4 py-3 border-t border-zinc-100 flex items-center justify-between">
          <div className="text-[10px] text-zinc-500">
            {rt === "leave" && "Condition tip: use 'days_gte' to add HR review only for longer leaves."}
            {rt === "reimbursement" && "Condition tip: use 'amount_gte' to require a second approver only for large claims."}
            {rt === "regularization" && "Condition tip: usually single-stage (reporting manager)."}
          </div>
          <Button onClick={save} disabled={busy || (chain.stages || []).length === 0} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="chain-save">
            <FloppyDisk size={14} className="mr-1" /> {busy ? "Saving…" : "Save chain"}
          </Button>
        </div>
      </div>

      {/* Preview */}
      {(chain.stages || []).length > 0 && (
        <div className="border border-zinc-200 rounded-sm bg-zinc-50 p-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Preview flow</div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="rounded-sm bg-white text-zinc-800 border-zinc-300">Request submitted</Badge>
            {chain.stages.map((s, i) => (
              <span key={i} className="flex items-center gap-2">
                <span className="text-zinc-400">→</span>
                <Badge variant="outline" className="rounded-sm bg-adb-navy/5 border-adb-navy/20 text-adb-navy">
                  {s.label || APPROVER_TYPES.find((a) => a.v === s.approver_type)?.label}
                  {s.condition && <span className="ml-1 text-[9px] text-adb-navy/60">({s.condition})</span>}
                </Badge>
              </span>
            ))}
            <span className="text-zinc-400">→</span>
            <Badge variant="outline" className="rounded-sm bg-adb-green/10 border-adb-green/40 text-adb-navy"><CheckCircle size={10} weight="fill" className="inline mr-1 text-adb-green" /> Approved</Badge>
          </div>
        </div>
      )}
    </div>
  );
}
